import {
  Group,
  Flex,
  Box,
  Text,
  Skeleton,
  ActionIcon,
  Avatar,
  useMantineTheme,
} from "@mantine/core";
import ActionCard from "src/components/ActionCard";
import {
  GetClientGuestLeadQuery,
  GetSignedAgreementsQuery,
} from "src/generated/graphql";
import { TabsName } from ".";
import { memo } from "react";
import { IoIosArrowForward } from "react-icons/io";
import TransactionAttendance from "../../TransactionAttendance";
import { AgreementsMap, EmptyData } from "../../ClientDetail/overview";
import { useParams } from "react-router-dom";
import NotesList from "../../ClientDetail/notes";
import { EMPLOYEE_ROLE_OBJ } from "src/utils/constants";

export type GuestOneQuery = GetClientGuestLeadQuery["clientOne"];

interface GuestOverProps {
  guestData: GuestOneQuery | undefined;
  onTabChange: (tabName: TabsName) => void;
  agreementData: GetSignedAgreementsQuery["signedAgreementMany"];
  onRefetch: () => void;
}

const GuestProfileOverView = ({
  guestData,
}: {
  guestData: Omit<NonNullable<GuestOneQuery>, "notes">;
}) => {
  const clientOverviewList = Object.entries({
    Name: `${guestData.user.firstName} ${guestData.user.lastName}`,
    DOB: guestData.dob,
    Email: guestData.user.email,
  });
  return (
    <Flex gap={"md"} align="center">
      <Avatar
        src={null}
        sx={{
          height: 100,
          width: 100,
          minHeight: 100,
          minWidth: 100,
          borderRadius: 50,
        }}
        alt="no image here"
      />
      <Box sx={{ width: "100%" }}>
        {clientOverviewList.map((clientEl, index) => (
          <Flex
            justify="space-between"
            align="center"
            mt={index > 0 ? 17 : 0}
            key={clientEl[1]}
          >
            <Text fw="bold">{clientEl![0]}:</Text> <Text>{clientEl![1]}</Text>
          </Flex>
        ))}
      </Box>
    </Flex>
  );
};

const GuestOverview = ({
  guestData,
  onTabChange,
  agreementData,
  onRefetch,
}: GuestOverProps) => {
  const theme = useMantineTheme();
  const { clientId } = useParams<{ clientId: string }>();

  if (!guestData || !agreementData) {
    return (
      <>
        <Skeleton height={50} circle mb="xl" />
        <Skeleton w="100%" height={10} mb={10} />
        <Skeleton w="100%" height={10} mb={10} />
        <Skeleton w="90%" height={10} mb={10} />
        <Skeleton w="80%" height={10} mb={10} />
        <Skeleton w="70%" height={10} mb={10} />
        <Skeleton w="50%" height={10} mb={10} />
        <Skeleton width="30%" height={10} radius="xl" />
      </>
    );
  }

  return (
    <>
      <Group noWrap spacing="xs" align="stretch">
        <ActionCard
          title="Profile Info"
          rightIconButton={
            <ActionIcon onClick={() => onTabChange(TabsName.PROFILE)}>
              <IoIosArrowForward />
            </ActionIcon>
          }
        >
          <GuestProfileOverView guestData={guestData} />
        </ActionCard>

        <ActionCard
          title="Account"
          rightIconButton={
            <ActionIcon
              onClick={() => onTabChange(TabsName.ACCOUNT)}
              sx={{
                color: theme.colors.primaryTextColor[6],
                "&:hover": {
                  backgroundColor: theme.colors.primary[4],
                },
              }}
            >
              <IoIosArrowForward />
            </ActionIcon>
          }
        >
          {!agreementData || agreementData.length == 0 ? (
            <EmptyData name="agreements" />
          ) : (
            <AgreementsMap agreements={agreementData} />
          )}
        </ActionCard>
      </Group>
      <TransactionAttendance onTabChange={onTabChange} />
      <NotesList
        notes={guestData.notes}
        onRefetch={onRefetch}
        clientId={clientId}
        roleType={EMPLOYEE_ROLE_OBJ.clients}
      />
    </>
  );
};

export default memo(GuestOverview);
